import { Box } from '@interstate/components/Box';
import { Grid } from '@interstate/components/Grid';
import { Spinner as LoadingIndicator } from '@interstate/components/Spinner';
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import * as flexBuyActionCreators from '@makemydeal/dr-dash-store/actions/flexBuyActionCreators';
import type { FlexBuyPaymentGridItem } from '@makemydeal/dr-dash-store/types/flexBuyPaymentsTypes';
import type { UpdateFlexBuyPaymentGridDataPayload } from '@makemydeal/dr-offer-redux';
import type { FC } from 'react';
import { useDispatch } from 'react-redux';
import { DownPaymentInputField } from './components/DownPaymentInputField';
import {
    LoadingIndicatorWrapper,
    StyledDivider,
    StyledGridItem,
    StyledMonthsTypography,
    StyledTypography
} from './styled.components';
import { FlexBuyAvailableMonths } from '@makemydeal/dr-platform-types';

type FlexBuyPaymentGridItemCellProps = {
    payments: UpdateFlexBuyPaymentGridDataPayload[number];
    flexBuyDownPayments: number[];
    flexBuyPercents: number[];
    flexBuyTerms: number[];
    isLoadingGrid?: boolean;
    index: number;
};

export const FlexBuyPaymentGridItemCell: FC<FlexBuyPaymentGridItemCellProps> = ({
    payments,
    flexBuyDownPayments,
    flexBuyPercents,
    flexBuyTerms,
    isLoadingGrid,
    index
}) => {
    const dispatch = useDispatch();

    const handleSelectPaymentGridItem = (item: FlexBuyPaymentGridItem) => () => {
        const isSelectedItem = item.isSelected;
        const isEmpty = item.isEmpty;

        if (isSelectedItem || isEmpty) return;

        dispatch(flexBuyActionCreators.thunkSelectFlexBuyPayment({ item, flexBuyDownPayments, flexBuyPercents, flexBuyTerms }));
    };

    return (
        <Grid
            display="contents"
            sx={{
                '& > div:first-of-type': {
                    background: '#f4f4f4'
                }
            }}
            data-testid={`${payments.key}-${payments.value}-${index}`}
        >
            <StyledGridItem
                xs={3}
                className={'payment-grid-item'}
                sx={{
                    height: '128px'
                }}
            >
                <DownPaymentInputField downPaymentValue={payments.value} rowIndex={payments.key} isLoading={isLoadingGrid} />
            </StyledGridItem>

            {payments.items.map((item) => (
                <StyledGridItem
                    xs={3}
                    className={'payment-grid-item'}
                    key={item.id}
                    sx={{
                        height: '128px'
                    }}
                    alignItems="center"
                    paddingLeft="1%"
                    data-testid={`flex-buy-payment-grid-item-row-${item.id}`}
                    $isSelected={item.isSelected && !item.isLoading}
                    $canSelect={!item.isEmpty && !item.isLoading && !item.isSelected}
                    onClick={handleSelectPaymentGridItem(item)}
                >
                    {item.isLoading && (
                        <LoadingIndicatorWrapper>
                            <LoadingIndicator data-testid={`loading-spinner-terms-col-${item.id}`} />
                        </LoadingIndicatorWrapper>
                    )}
                    {!item.isLoading && !item.isEmpty && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexDirection: 'column'
                            }}
                        >
                            <StyledMonthsTypography $isSelected={item.isSelected}>1–36 mo</StyledMonthsTypography>
                            <StyledTypography className="large" data-testid={`monthly-payment-title-${item.id}`}>
                                {formatDollarsAndCents(item.firstTermPaymentDetail.paymentAmount)}
                            </StyledTypography>

                            <StyledDivider $isSelected={item.isSelected} />

                            <StyledMonthsTypography $isSelected={item.isSelected}>
                                37–{item.term === FlexBuyAvailableMonths.MONTH_66 ? 66 : 75} mo
                            </StyledMonthsTypography>

                            <StyledTypography className="large">
                                {formatDollarsAndCents(item.secondTermPaymentDetail.paymentAmount)}
                            </StyledTypography>
                        </Box>
                    )}

                    {!item.isLoading && item.isEmpty && (
                        <StyledTypography data-testid={`no-payment-available-${item.id}`} className="large">
                            No Payment Available
                        </StyledTypography>
                    )}
                </StyledGridItem>
            ))}
        </Grid>
    );
};
